import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Redirect } from 'react-router';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import some from 'lodash/some';

import { DisplayComponent } from 'formlabs-web-components/BaseComponents';
import { Link } from 'formlabs-web-components/Link';
import { Grid, Cell, GridContainer } from 'formlabs-web-components/Grid';

import MobileSidescrollMenu from './MobileSidescrollMenu';
import StoreButton from '../storefront/MenuButton';
import useObserver from '../../hooks/useObserver';

import useIsMobile from '../../hooks/useIsMobile';

import './HeaderMenu.module.css';

const StoreMenu = ({ baseUrl, menu, isWholesale = false }) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [stuck, setStuck] = useState(false);
  const [navElement, setNavElement] = useState(null);
  const { pathname } = useLocation();

  const navRefCallback = useCallback(node => {
    if (node !== null) {
      setNavElement(node);
    }
  }, []);

  const memoizedCallback = useCallback(nav => {
    setStuck(!nav[0].isIntersecting);
  }, []);

  const [attach, detach] = useObserver(memoizedCallback, {
    rootMargin: '-50px'
  });

  useEffect(
    () => {
      if (navElement) {
        attach(navElement.parentElement);
      }
      return () => {
        if (navElement) {
          detach(navElement.parentElement);
        }
      };
    },
    [navElement]
  );

  // make sure to re-attach the observer after resize/re-orientation
  useEffect(
    () => {
      if (navElement) {
        setStuck(false);
        detach(navElement.parentElement);
        attach(navElement.parentElement);
      }
    },
    [navElement]
  );

  // Redirect store homepage for wholesale users
  if (isWholesale && baseUrl === pathname) {
    return <Redirect to={`${baseUrl}wholesale/`} />;
  }

  const menuEntries = menu.map(entry => ({
    url: `${baseUrl}${entry.url}/`,
    name: entry.name,
    active: entry.active
  }));

  const newMenu = [
    {
      url: baseUrl,
      name: t('Home'),
      active: !some(menuEntries, 'active')
    },
    ...menuEntries
  ];

  if (isMobile) {
    return <MobileSidescrollMenu menu={newMenu} />;
  }

  return (
    <div style={(stuck && { height: '59px' }) || null}>
      <nav
        styleName={classnames('page-sub-nav', 'page-color-black', 'in-store', {
          stuck
        })}
        aria-label={t('Second level page navigation')}
        style={{ marginBottom: '2rem' }}
        ref={navRefCallback}
      >
        <GridContainer styles={{ maxWidth: '1920px' }} containerType="fluid">
          <div styleName={classnames('bottom-border', 'hide-border')}>
            <Grid direction="x" hasContainer={false}>
              <Cell mediumSize={2}>
                <p styleName="page-title">
                  {isWholesale ? t('Wholesale') : t('Store')}
                </p>
              </Cell>
              <Cell mediumSize={20}>
                {!isWholesale && (
                  <ul styleName="page-sub-nav-right">
                    {newMenu.map(item => (
                      <li styleName="sub-nav-item" key={item.url}>
                        <Link
                          to={item.url}
                          title={item.name}
                          styleName={classnames('sub-nav-link', {
                            'current-page': item.active
                          })}
                        >
                          {item.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </Cell>
              <Cell mediumSize={2}>
                {stuck && (
                  <div style={{ textAlign: 'right' }}>
                    <StoreButton hideButton={true} />
                  </div>
                )}
              </Cell>
            </Grid>
          </div>
        </GridContainer>
      </nav>
    </div>
  );
};

StoreMenu.propTypes = {
  baseUrl: PropTypes.string.isRequired
};

export default DisplayComponent(StoreMenu);
